import { Helmet } from 'react-helmet';
import { useLocation } from 'react-router-dom';
import { canonicalUrl } from '../lib/seo.mjs';

/**
 * THE single declaration site for the self-referencing canonical URL — and for
 * the two identity tags that must always agree with it (og:url, twitter:url).
 *
 * WHY THIS IS CENTRAL (the bug it exists to kill):
 * canonicals used to be declared per page, inside each page's own <Helmet>.
 * Eleven pages did it; the other eleven prerendered routes — INCLUDING THE
 * HOMEPAGE — simply forgot, and nothing failed. index.html deliberately carries
 * no site-wide canonical (a fixed one would mis-collapse every sub-page), so
 * "the page forgot" meant the emitted HTML contained ZERO canonical tags and
 * Google was left to pick a canonical on its own. Verified live on dev+prod
 * 2026-07-27: /, /shingles/, /about/, /reviews/, /service-areas/ and
 * /privacy-policy/ all shipped with no canonical at all.
 *
 * A per-page opt-in cannot be made safe — every new route is one forgotten line
 * away from the same defect. So the canonical is derived from the ROUTE instead:
 * mounted once inside SiteRoutes, it covers every route that exists today and
 * every route added later, with no per-page action required. Pages must NOT
 * declare their own rel="canonical"; scripts/check-canonical-tags.mjs fails the
 * build if a second one ever reappears in the emitted HTML.
 *
 * The URL always comes from canonicalUrl() (src/lib/seo.mjs) — absolute, apex
 * host, trailing slash, which is the ONLY spelling that returns 200 (prerendered
 * routes and static hubs are served from directories; nginx 301s the bare path).
 * useLocation().pathname carries no query string, so UTM-tagged landings still
 * canonicalise to the clean URL.
 */
const CanonicalTags = () => {
  const { pathname } = useLocation();

  // canonicalUrl() is inlined at each of the three call sites rather than
  // hoisted into a local: it is pure and trivial, and scripts/check-seo-urls.mjs
  // statically requires every rel="canonical" href to go through it (a local
  // would read as a hand-written URL to that gate). Keeping the literal call
  // keeps BOTH gates honest.
  return (
    <Helmet>
      <link rel="canonical" href={canonicalUrl(pathname)} />
      {/* og:url / twitter:url are the same identity claim as the canonical, so
          they are emitted from the same value rather than left to index.html's
          static homepage URL — which is what made every sub-page share
          https://openbooq.com/ before this component existed. The prerenderer
          strips the shell's static copy of any meta a route sets (see
          applyHelmet in scripts/prerender.mjs), so these replace, not duplicate. */}
      <meta property="og:url" content={canonicalUrl(pathname)} />
      <meta property="twitter:url" content={canonicalUrl(pathname)} />
    </Helmet>
  );
};

export default CanonicalTags;
